import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Input, 
  Switch, 
  Table, 
  Button, 
  Space, 
  Modal, 
  Form, 
  message,
  Card,
  Typography,
  Divider,
  Tag,
  Alert,
  Select,
  DatePicker,
  Tooltip
} from 'antd';
import type { InputRef } from 'antd';
import { 
  ScanOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  PlusOutlined,
  ClearOutlined,
  CheckOutlined,
  DownloadOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import request from '../../../../utils/request';
import { getProjectList, getCustomerList } from '../../../../services/project';
import TimezoneAwareTime from '../../../../components/TimezoneAwareTime';
import DownloadFieldsModal from './components/DownloadFieldsModal';
import { usePermissions } from '../../../../contexts/PermissionContext';
import { isLocalSpeechSupported, speakTextLocal } from '../../../../utils/localSpeech';
import type { LocalSpeechLang } from '../../../../utils/localSpeech';

// 上架扫描播报：语言和扫描入库页共用一个设置；"关闭"只对上架页生效，单独保存
const SPEECH_LANG_STORAGE_KEY = 'local-speech-lang';
const PUT_AWAY_SPEECH_OFF_STORAGE_KEY = 'put-away-speech-off';
type PutAwaySpeechMode = LocalSpeechLang | 'off';
const ZH_DIGITS = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九'];
const EN_DIGITS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];

// 取容器编码最后一个"-"后面的部分，逐位读出（SC-B-26-1061 读"一 零 六 一"，不读"一千零六十一"）；字母逐个读
export const containerCodeSpeechText = (code: string, lang: LocalSpeechLang): string => {
  const suffix = String(code || '').trim().split('-').pop() || '';
  return suffix
    .toUpperCase()
    .split('')
    .map((ch) => {
      if (/[0-9]/.test(ch)) return (lang === 'zh-CN' ? ZH_DIGITS : EN_DIGITS)[Number(ch)];
      return /[A-Z]/.test(ch) ? ch : '';
    })
    .filter(Boolean)
    .join(' ');
};

// 仓位编码固定用英文读：数字逐位读，单个字母按字母读，连续字母当单词读，"-" 读 dash（G10-2 读 "G one zero dash two"，E1-1 side 读 "E one dash one side"）
export const locationCodeSpeechText = (code: string): string => {
  const parts = String(code || '').trim().match(/[A-Za-z]+|[0-9]|-/g) || [];
  return parts
    .map((part) => {
      if (part === '-') return 'dash';
      if (/^[0-9]$/.test(part)) return EN_DIGITS[Number(part)];
      return part.length === 1 ? part.toUpperCase() : part.toLowerCase();
    })
    .join(' ');
};

const loadPutAwaySpeechMode = (): PutAwaySpeechMode => {
  try {
    if (localStorage.getItem(PUT_AWAY_SPEECH_OFF_STORAGE_KEY) === '1') return 'off';
    const lang = String(localStorage.getItem(SPEECH_LANG_STORAGE_KEY) || '').trim();
    if (lang === 'zh-CN' || lang === 'en-US') return lang;
  } catch {}
  return 'zh-CN';
};

const { Title, Text } = Typography;
const { Search } = Input;

interface PutAwayRecord {
  id: string;
  containerCode: string;
  locationCode: string;
  projectCode?: string;
  projectName?: string;
  customerId?: number;
  customerUsername?: string;
  warehouseName?: string;
  putAwayTime: string;
  putAwayTimeRaw?: string;
  operator: string;
  status: 'completed' | 'pending' | 'cancelled';
  remarks?: string;
}

interface ScanItem {
  value: string;
  type: 'container' | 'location';
  timestamp: Date;
}

interface IocPutAwayRecord {
  id: string;
  iocCode: string;
  instructionCode: string;
  instructionId: number;
  warehouseName: string;
  locationDisplay: string;
  operator: string;
  remarks?: string;
  statusDisplay: string;
  updatedAt: string;
  updatedAtRaw?: string;
}

const PutAway: React.FC = () => {
  const { t } = useTranslation();
  const { hiddenFields, exportHiddenFields, isWarehouseStaff, isWarehouseSupervisor, permissions } = usePermissions();
  const [continuousScan, setContinuousScan] = useState(false);
  const [scanValue, setScanValue] = useState('');
  const [scanHistory, setScanHistory] = useState<ScanItem[]>([]);
  const [currentBatch, setCurrentBatch] = useState<{
    containers: string[];
    location: string | null;
  }>({ containers: [], location: null });
  const [records, setRecords] = useState<PutAwayRecord[]>([
    // 模拟数据
    {
      id: '1',
      containerCode: 'CTN001',
      locationCode: 'A-01-01-01',
      putAwayTime: '2024-01-15 14:30:25',
      operator: '张三',
      status: 'completed'
    },
    {
      id: '2',
      containerCode: 'CTN002',
      locationCode: 'A-01-01-02',
      putAwayTime: '2024-01-15 14:25:18',
      operator: '李四',
      status: 'completed'
    },
    {
      id: '3',
      containerCode: 'CTN003',
      locationCode: 'A-01-01-03',
      putAwayTime: '2024-01-15 14:20:12',
      operator: '王五',
      status: 'pending'
    }
  ]);
  
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<PutAwayRecord | null>(null);
  const [editForm] = Form.useForm();
  const [isScanning, setIsScanning] = useState(false); // 添加扫描状态
  const scanInputRef = useRef<InputRef>(null); // 添加扫描输入框引用
  
  // 新增：创建新仓位的状态
  const [isCreateLocationModalVisible, setIsCreateLocationModalVisible] = useState(false);
  const [createLocationForm] = Form.useForm();
  
  // 新增：仓库列表状态
  const [warehouses, setWarehouses] = useState<Array<{
    id: number;
    code: string;
    name: string;
    country: string;
  }>>([]);
  const [isLoadingWarehouses, setIsLoadingWarehouses] = useState(false);

  // 新增：仓位列表状态
  const [locations, setLocations] = useState<Array<{
    id: number;
    code: string;
    warehouse_name: string;
    location_type: string;
    location_attribute: string;
  }>>([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  
  // 新增：仓位选择器的key，用于强制重新渲染
  const [locationSelectKey, setLocationSelectKey] = useState(0);
  
  // 新增：所有容器列表状态
  const [allContainers, setAllContainers] = useState<Array<{
    id: number;
    system_code: string;
    name: string;
    current_location: string | null;
  }>>([]);
  const [isLoadingContainers, setIsLoadingContainers] = useState(false);

  // 新增：表格分页与筛选状态
  const [tablePagination, setTablePagination] = useState<{ current: number; pageSize: number; total: number }>({
    current: 1,
    pageSize: 15,
    total: 0,
  });
  const [tableFilters, setTableFilters] = useState<{ customerId?: string; projectCode?: string; containerCode?: string; locationCode?: string; warehouseCode?: string }>({
    customerId: '',
    projectCode: '',
    containerCode: '',
    locationCode: '',
    warehouseCode: ''
  });
  const [tableDateRange, setTableDateRange] = useState<[any, any] | null>(null);
  const [activeTab, setActiveTab] = useState<'storage' | 'ioc'>('storage');
  // 最近一次扫描成功的容器或仓位（大字显示，和播报内容始终一致）和播报设置
  const [lastScScan, setLastScScan] = useState<{ kind: 'container' | 'location'; code: string } | null>(null);
  const [lastIocScan, setLastIocScan] = useState<{ kind: 'container' | 'location'; code: string } | null>(null);
  const [speechSupported] = useState(() => isLocalSpeechSupported());
  const [speechMode, setSpeechMode] = useState<PutAwaySpeechMode>(() => loadPutAwaySpeechMode());
  const changeSpeechMode = (mode: PutAwaySpeechMode) => {
    setSpeechMode(mode);
    try {
      localStorage.setItem(PUT_AWAY_SPEECH_OFF_STORAGE_KEY, mode === 'off' ? '1' : '0');
      if (mode !== 'off') localStorage.setItem(SPEECH_LANG_STORAGE_KEY, mode);
    } catch {}
  };
  // 容器成功加入当前批次、或仓位开始提交上架时调用：大字显示并播报。
  // 容器只读编码最后一段，按选择的语言；仓位读完整编码，固定用英文
  const announceScan = (tab: 'storage' | 'ioc', kind: 'container' | 'location', code: string) => {
    if (tab === 'storage') setLastScScan({ kind, code });
    else setLastIocScan({ kind, code });
    if (!speechSupported || speechMode === 'off') return;
    const lang: LocalSpeechLang = kind === 'location' ? 'en-US' : speechMode;
    const text = kind === 'location' ? locationCodeSpeechText(code) : containerCodeSpeechText(code, lang);
    if (text) speakTextLocal(text, { lang, rate: 1, pitch: 1, volume: 1 });
  };
  // 大字：容器编码的最后一段黄底红字；仓位显示完整编码
  const renderLastScan = (scan: { kind: 'container' | 'location'; code: string } | null, className: string) => {
    if (!scan) return null;
    const cut = scan.code.lastIndexOf('-');
    const big: React.CSSProperties = { fontSize: 56, fontWeight: 700, lineHeight: 1.1, fontFamily: 'Consolas, Menlo, monospace', whiteSpace: 'nowrap' };
    return (
      <span className={className} data-kind={scan.kind} style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 14, color: '#8c8c8c', whiteSpace: 'nowrap' }}>{scan.kind === 'location' ? '仓位' : '容器'}</span>
        {scan.kind === 'location' ? (
          <span className="put-away-last-scan-code" style={{ ...big, color: '#0958d9' }}>{scan.code}</span>
        ) : (
          <span className="put-away-last-scan-code" style={{ ...big, color: '#1f1f1f' }}>
            {cut >= 0 ? scan.code.slice(0, cut + 1) : ''}
            <span className="put-away-last-scan-suffix" style={{ background: '#fadb14', color: '#cf1322', padding: '0 8px', borderRadius: 4 }}>
              {cut >= 0 ? scan.code.slice(cut + 1) : scan.code}
            </span>
          </span>
        )}
      </span>
    );
  };
  const [iocScanValue, setIocScanValue] = useState('');
  const [iocScanHistory, setIocScanHistory] = useState<ScanItem[]>([]);
  const [iocCurrentBatch, setIocCurrentBatch] = useState<{
    containers: string[];
    location: string | null;
    instructionId: number | null;
    instructionCode: string | null;
  }>({ containers: [], location: null, instructionId: null, instructionCode: null });
  const [iocIsScanning, setIocIsScanning] = useState(false);
  const iocScanInputRef = useRef<InputRef>(null);
  const [iocRecords, setIocRecords] = useState<IocPutAwayRecord[]>([]);
  const [iocTablePagination, setIocTablePagination] = useState<{ current: number; pageSize: number; total: number }>({
    current: 1,
    pageSize: 15,
    total: 0,
  });
  const [iocTableFilters, setIocTableFilters] = useState<{ iocCode?: string; locationCode?: string; instructionCode?: string }>({
    iocCode: '',
    locationCode: '',
    instructionCode: '',
  });
  const iocFuzzySearchTimerRef = useRef<any>(null);
  const [isIocEditModalVisible, setIsIocEditModalVisible] = useState(false);
  const [iocEditingRecord, setIocEditingRecord] = useState<IocPutAwayRecord | null>(null);
  const [iocEditForm] = Form.useForm();
  // 模糊实时搜索防抖
  const fuzzySearchTimerRef = useRef<any>(null);
  const projectSearchTimerRef = useRef<any>(null);
  const selectSearchTimerRef = useRef<any>(null);
  const [projectOptions, setProjectOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [projectLoading, setProjectLoading] = useState(false);
  const [customerOptions, setCustomerOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [downloadVisible, setDownloadVisible] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // 敏感字段：所属客户隐藏判定
  const hidden = hiddenFields && hiddenFields('storage.ContainerPutAwayRecord');
  const hideCustomer = Array.isArray(hidden) && (
    hidden.includes('customer_username') ||
    hidden.includes('project__customer__username') ||
    hidden.includes('container.project.customer') ||
    hidden.includes('customer')
  );
  const exportHidden = exportHiddenFields && exportHiddenFields('storage.ContainerPutAwayRecord');

  // 仅用于“创建新仓位”模态框的可选仓库列表（仓库员工/主管仅显示被分配的一个仓库）
  const assignedWarehouseIds = (permissions?.assigned_warehouse_ids || []) as number[];
  const restrictedByWarehouseRole = isWarehouseStaff() || isWarehouseSupervisor();
  const warehousesForModal = restrictedByWarehouseRole
    ? warehouses.filter(w => assignedWarehouseIds.includes(w.id))
    : warehouses;

  // 列表筛选用的仓库数据（与模态框一致的权限过滤）
  const warehousesForFilter = warehousesForModal;

  // 当为仓库员工/主管时，若未选择仓库，则默认选择被分配的第一个仓库（按 code）
  useEffect(() => {
    if (restrictedByWarehouseRole && !tableFilters.warehouseCode && warehousesForFilter.length > 0) {
      setTableFilters(prev => ({ ...prev, warehouseCode: warehousesForFilter[0].code }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restrictedByWarehouseRole, warehousesForFilter.length]);

  // 当策略隐藏客户时，清空客户筛选，避免无效参数外传
  useEffect(() => {
    if (hideCustomer && tableFilters.customerId) {
      setTableFilters(prev => ({ ...prev, customerId: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideCustomer]);

  const normalizeScScanValue = (value: string): string => String(value || '').trim().toUpperCase();

  // 识别扫描值类型
  const identifyScanType = (value: string): 'container' | 'location' => {
    const upperValue = normalizeScScanValue(value);
    const isContainer = upperValue.startsWith('SC');
    const result = isContainer ? 'container' : 'location';
    
    console.log('=== 扫描值类型识别 ===');
    console.log('原始值:', value);
    console.log('大写值:', upperValue);
    console.log('是否以SC开头:', isContainer);
    console.log('识别结果:', result);
    console.log('=== 类型识别完成 ===');
    
    return result;
  };

  const scanAudioCtxRef = useRef<any>(null);
  const playScanAlertBeep = useCallback(() => {
    try {
      const AudioCtx: any = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      void (async () => {
        try {
          let ctx = scanAudioCtxRef.current;
          if (!ctx) {
            ctx = new AudioCtx();
            scanAudioCtxRef.current = ctx;
          }
          try { if (ctx.state === 'suspended') await ctx.resume(); } catch {}
          const startAt = ctx.currentTime + 0.01;
          const pulses = [
            { f1: 1400, f2: 800, dur: 0.22, gap: 0.00 },
          ];
          let t = startAt;
          for (const p of pulses) {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'square';
            osc.frequency.setValueAtTime(p.f1, t);
            osc.frequency.linearRampToValueAtTime(p.f2, t + p.dur);
            gain.gain.setValueAtTime(0.0001, t);
            gain.gain.exponentialRampToValueAtTime(0.4, t + 0.01);
            gain.gain.exponentialRampToValueAtTime(0.0001, t + p.dur);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(t);
            osc.stop(t + p.dur + 0.02);
            t = t + p.dur + p.gap;
          }
        } catch {}
      })();
    } catch {}
  }, []);

  const scanWarn = (msg: string) => {
    playScanAlertBeep();
    message.warning(msg);
  };
  const scanError = (msg: string) => {
    playScanAlertBeep();
    message.error(msg);
  };

  const copyCode = async (text: string) => {
    const val = String(text || '').trim();
    if (!val) return;
    try {
      await navigator.clipboard.writeText(val);
      message.success('已复制');
    } catch {
      message.error('复制失败');
    }
  };

  // IOC 扫码类型识别：IOC开头视为容器，其余视为仓位输入
  const identifyIocScanType = (value: string): 'container' | 'location' => {
    const upperValue = String(value || '').trim().toUpperCase();
    return upperValue.startsWith('IOC') ? 'container' : 'location';
  };

  const validateIocExists = async (iocCode: string): Promise<{
    exists: boolean;
    code?: string;
    instructionId?: number;
    instructionCode?: string;
    locationDisplay?: string | null;
    statusDisplay?: string;
  }> => {
    try {
      const code = String(iocCode || '').trim().toUpperCase();
      if (!code) return { exists: false };
      const resp = await request.get('/api/v1/storage/containers/unified/', {
        params: {
          container_kind: 'output',
          system_code: code,
          exclude_active_reservations: false,
          page: 1,
          page_size: 50,
        },
      });
      const rows: any[] = Array.isArray(resp?.data?.results) ? resp.data.results : [];
      const hit = rows.find((r: any) => String(r?.system_code || '').trim().toUpperCase() === code);
      if (!hit) return { exists: false };
      return {
        exists: true,
        code,
        instructionId: Number(hit?.instruction_id || 0) || undefined,
        instructionCode: String(hit?.instruction_code || '').trim() || undefined,
        locationDisplay: String(hit?.current_location_display || '').trim() || null,
        statusDisplay: String(hit?.status_display || '').trim() || String(hit?.status || '').trim() || '-',
      };
    } catch {
      return { exists: false };
    }
  };

  const fetchIocPutAwayRecords = async (
    page = iocTablePagination.current,
    pageSize = iocTablePagination.pageSize,
  ) => {
    try {
      const params: any = {
        page,
        page_size: pageSize,
      };
      if (iocTableFilters.iocCode) params.ioc_code = iocTableFilters.iocCode;
      if (iocTableFilters.locationCode) params.location_code = iocTableFilters.locationCode;
      if (iocTableFilters.instructionCode) params.instruction_code = iocTableFilters.instructionCode;
      const response = await request.get('/api/v1/storage/output-containers/put-away-records/', { params });
      const rows: any[] = Array.isArray(response?.data?.data?.records) ? response.data.data.records : [];
      const total = Number(response?.data?.data?.total || 0);
      const formatted: IocPutAwayRecord[] = rows.map((row: any) => ({
        id: String(row?.id || ''),
        iocCode: String(row?.ioc_code || '').trim(),
        instructionCode: String(row?.instruction_code || '-').trim() || '-',
        instructionId: Number(row?.instruction_id || 0),
        warehouseName: String(row?.warehouse_name || '-').trim() || '-',
        locationDisplay: String(row?.location_display || '').trim() || '-',
        operator: String(row?.operator_name || '-').trim() || '-',
        remarks: String(row?.remarks || '').trim(),
        statusDisplay: String(row?.status_display || row?.status || '-').trim() || '-',
        updatedAt: row?.put_away_time ? new Date(row.put_away_time).toLocaleString('zh-CN') : '-',
        updatedAtRaw: row?.put_away_time,
      }));
      setIocRecords(formatted);
      setIocTablePagination((prev) => ({
        ...prev,
        current: Number(response?.data?.data?.page || page),
        pageSize: Number(response?.data?.data?.page_size || pageSize),
        total,
      }));
    } catch (error: any) {
      console.warn('获取 IOC 仓位记录失败:', error?.message || error);
    }
  };

  const submitIocPutAway = async (locationInput: string) => {
    const loc = String(locationInput || '').trim();
    const { containers, instructionId } = iocCurrentBatch;
    if (!containers.length) {
      setIocScanValue('');
      setTimeout(() => iocScanInputRef.current?.focus(), 0);
      return;
    }
    if (!(Number(instructionId) > 0)) {
      message.error('当前批次缺少来源指令，无法提交 IOC 仓位');
      return;
    }
    try {
      await request.post('/api/v1/storage/output-containers/put-away/', {
        instruction_id: instructionId,
        codes: containers,
        location_input: loc,
      });
      message.success(`已提交 ${containers.length} 个 IOC 的仓位`);
      await fetchIocPutAwayRecords(1, iocTablePagination.pageSize);
      setIocCurrentBatch({ containers: [], location: null, instructionId: null, instructionCode: null });
      setIocScanHistory((prev) => prev.filter((item) => item.type !== 'location' && !containers.includes(item.value)));
      setIocScanValue('');
      setTimeout(() => iocScanInputRef.current?.focus(), 0);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || '提交 IOC 仓位失败');
    }
  };

  const handleIocEdit = (record: IocPutAwayRecord) => {
    setIocEditingRecord(record);
    iocEditForm.setFieldsValue({
      iocCode: record.iocCode,
      locationCode: record.locationDisplay && record.locationDisplay !== '-' ? record.locationDisplay : undefined,
      remarks: record.remarks || '',
    });
    setIsIocEditModalVisible(true);
  };

  const handleSaveIocEdit = async () => {
    try {
      const values = await iocEditForm.validateFields();
      if (!iocEditingRecord) return;
      const loc = String(values.locationCode || '').trim();
      if (!loc) {
        message.error('请输入仓位编码');
        return;
      }
      await request.post('/api/v1/storage/output-containers/put-away/', {
        instruction_id: iocEditingRecord.instructionId,
        codes: [iocEditingRecord.iocCode],
        location_input: loc,
        remarks: String(values.remarks || '').trim(),
      });
      message.success('IOC 仓位更新成功');
      setIsIocEditModalVisible(false);
      setIocEditingRecord(null);
      iocEditForm.resetFields();
      await fetchIocPutAwayRecords(iocTablePagination.current, iocTablePagination.pageSize);
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.detail || error?.message || '更新 IOC 仓位失败');
    }
  };

  const handleIocScan = async (value: string) => {
    const raw = String(value || '').trim();
    if (!raw) {
      scanWarn('请输入 IOC 编码或仓位');
      return;
    }
    if (iocIsScanning) {
      scanWarn('正在处理扫描，请稍候...');
      return;
    }
    const scanType = identifyIocScanType(raw);
    const scanItem: ScanItem = { value: raw.toUpperCase(), type: scanType, timestamp: new Date() };
    if (scanType === 'container') {
      setIocIsScanning(true);
      try {
        const hit = await validateIocExists(raw);
        if (!hit.exists || !hit.code) {
          scanError('IOC 不存在，请检查编码');
          setIocScanValue('');
          setTimeout(() => iocScanInputRef.current?.focus(), 0);
          return;
        }
        if (!(Number(hit.instructionId || 0) > 0)) {
          scanError('无法识别 IOC 对应指令，暂不可提交仓位');
          setIocScanValue('');
          setTimeout(() => iocScanInputRef.current?.focus(), 0);
          return;
        }
        if (hit.locationDisplay) {
          scanWarn(`IOC 已有仓位（${hit.locationDisplay}），请确认是否需要重新登记`);
        }
        if (iocCurrentBatch.containers.includes(hit.code)) {
          scanWarn('重复扫描：该 IOC 已在当前批次中');
          setIocScanValue('');
          setTimeout(() => iocScanInputRef.current?.focus(), 0);
          return;
        }
        const currentInstructionId = Number(iocCurrentBatch.instructionId || 0);
        if (currentInstructionId > 0 && currentInstructionId !== Number(hit.instructionId)) {
          scanWarn('当前批次仅允许同一来源指令的 IOC，请先提交或清空批次');
          setIocScanValue('');
          setTimeout(() => iocScanInputRef.current?.focus(), 0);
          return;
        }
        setIocScanHistory((prev) => [...prev, scanItem]);
        setIocCurrentBatch((prev) => ({
          containers: [...prev.containers, hit.code as string],
          location: prev.location,
          instructionId: Number(hit.instructionId),
          instructionCode: String(hit.instructionCode || '-'),
        }));
        announceScan('ioc', 'container', String(hit.code));
        setIocScanValue('');
        setTimeout(() => iocScanInputRef.current?.focus(), 0);
      } finally {
        setIocIsScanning(false);
      }
      return;
    }

    if (!iocCurrentBatch.containers.length) {
      scanWarn('请先扫描 IOC，再扫描/输入仓位');
      setIocScanValue('');
      setTimeout(() => iocScanInputRef.current?.focus(), 0);
      return;
    }
    const loc = raw.toUpperCase();
    announceScan('ioc', 'location', loc);
    setIocCurrentBatch((prev) => ({ ...prev, location: loc }));
    setIocScanHistory((prev) => [...prev, { ...scanItem, value: loc }]);
    await submitIocPutAway(loc);
  };

  // 处理扫描输入
  const handleScan = async (value: string) => {
    console.log('=== 扫描处理开始 ===');
    console.log('扫描输入值:', value);
    console.log('扫描输入类型:', typeof value);
    console.log('扫描输入长度:', value.length);
    
    const normalizedValue = normalizeScScanValue(value);
    if (!normalizedValue) {
      console.log('扫描值为空，显示警告');
      scanWarn(t('putAway.messages.inputWarning'));
      return;
    }

    if (isScanning) {
      console.log('正在扫描中，显示警告');
      scanWarn('正在处理扫描，请稍候...');
      return;
    }

    const scanType = identifyScanType(normalizedValue);
    console.log('扫描类型识别结果:', scanType);
    console.log('当前批次状态:', currentBatch);
    
    const scanItem: ScanItem = {
      value: normalizedValue,
      type: scanType,
      timestamp: new Date()
    };
    console.log('创建的扫描项:', scanItem);

    if (scanType === 'container') {
      console.log('识别为容器，开始验证...');
      // 扫描到容器，立即进行验证
      setIsScanning(true);
      try {
        console.log('=== 开始容器验证 ===');
        
        // 1. 验证容器是否存在
        console.log('步骤1: 验证容器是否存在');
        const containerExists = await validateContainerExists(normalizedValue);
        console.log('容器存在性验证结果:', containerExists);
        
        if (!containerExists.exists) {
          console.log('容器不存在，清空扫描框并聚焦');
          scanError('容器不存在，请检查编码');
          // 清空扫描框并聚焦，不显示提示信息
          setScanValue('');
          // 使用setTimeout确保状态更新后再聚焦
          setTimeout(() => {
            scanInputRef.current?.focus();
          }, 0);
          return;
        }
        console.log('容器存在性验证通过');

        // 2. 检查容器是否已有仓位（已上架）
        console.log('步骤2: 检查容器是否已有仓位');
        console.log('容器当前仓位:', containerExists.current_location);
        
        if (containerExists.current_location) {
          console.log('容器已有仓位，清空扫描框并聚焦');
          scanWarn(`容器已上架（${containerExists.current_location}），无需重复上架`);
          // 清空扫描框并聚焦，不显示提示信息
          setScanValue('');
          setTimeout(() => {
            scanInputRef.current?.focus();
          }, 0);
          return;
        }
        console.log('容器仓位状态验证通过');

        // 3. 检查是否重复扫描
        console.log('步骤3: 检查是否重复扫描');
        console.log('当前批次容器列表:', currentBatch.containers);
        console.log(
          '是否已包含当前容器:',
          currentBatch.containers.some((code) => normalizeScScanValue(code) === normalizedValue),
        );
        
        if (currentBatch.containers.some((code) => normalizeScScanValue(code) === normalizedValue)) {
          console.log('容器重复扫描，清空扫描框并聚焦');
          scanWarn('重复扫描：该容器已在当前批次中');
          // 清空扫描框并聚焦，不显示提示信息
          setScanValue('');
          setTimeout(() => {
            scanInputRef.current?.focus();
          }, 0);
          return;
        }
        console.log('重复扫描检查通过');

        // 验证通过，添加到扫描历史和当前批次
        console.log('所有验证通过，开始更新状态');
        setScanHistory(prev => [...prev, scanItem]);
        setCurrentBatch(prev => ({
          ...prev,
          containers: [...prev.containers, normalizedValue]
        }));
        announceScan('storage', 'container', normalizedValue);
        console.log('状态更新完成，清空扫描框并聚焦');
        // 清空扫描框并聚焦，不显示成功提示
        setScanValue('');
        setTimeout(() => {
          scanInputRef.current?.focus();
        }, 0);
        
        console.log('=== 容器验证完成 ===');
      } catch (error) {
        console.error('容器验证过程中发生错误:', error);
        // 验证失败时提示错误并清空聚焦
        scanError('容器验证失败，请稍后重试');
        setScanValue('');
        setTimeout(() => {
          scanInputRef.current?.focus();
        }, 0);
        return;
      } finally {
        console.log('设置扫描状态为false');
        setIsScanning(false);
      }
    } else {
      console.log('识别为仓位，检查批次状态');
      // 扫描到仓位
      if (currentBatch.containers.length === 0) {
        console.log('当前批次无容器，清空扫描框并聚焦');
        scanWarn('请先扫描容器，再扫描仓位');
        // 清空扫描框并聚焦，不显示提示信息
        setScanValue('');
        setTimeout(() => {
          scanInputRef.current?.focus();
        }, 0);
        return;
      }
      
      console.log('仓位验证通过，更新批次状态');
      setCurrentBatch(prev => ({
        ...prev,
        location: normalizedValue
      }));
      
      console.log('开始创建上架记录');
      // 创建上架记录（先大字显示并播报仓位）
      announceScan('storage', 'location', normalizedValue);
      createPutAwayRecords(normalizedValue);
    }

    console.log('=== 扫描处理完成 ===');
  };

  // 验证容器是否存在
  const validateContainerExists = async (containerCode: string): Promise<{
    exists: boolean;
    current_location: string | null;
  }> => {
    const normalizedContainerCode = normalizeScScanValue(containerCode);
    console.log('=== 容器验证开始 ===');
    console.log('验证容器编码:', normalizedContainerCode);
    
    try {
      const apiUrl = '/api/v1/storage/containers/';
      let allContainers: any[] = [];
      let nextUrl: string | null = `${apiUrl}?system_code=${encodeURIComponent(normalizedContainerCode)}`;
      let pageCount = 0;
      
      console.log('开始跨分页验证...');
      
      // 遍历所有分页数据
      while (nextUrl) {
        pageCount++;
        console.log(`正在验证第 ${pageCount} 页...`);
        
        const response = await request.get(nextUrl);
        console.log(`第 ${pageCount} 页响应:`, {
          status: response.status,
          count: response.data.count,
          results: response.data.results?.length || 0,
          next: response.data.next
        });
        
        if (response.data.results && response.data.results.length > 0) {
          allContainers = [...allContainers, ...response.data.results];
          console.log(`第 ${pageCount} 页添加了 ${response.data.results.length} 个容器，累计 ${allContainers.length} 个`);
        }
        
        // 获取下一页URL
        nextUrl = response.data.next;
        
        // 安全检查：防止无限循环
        if (pageCount > 100) {
          console.warn('分页验证超过100页，停止继续验证');
          break;
        }
      }
      
      console.log('分页验证完成，总容器数量:', allContainers.length);
      console.log('所有容器编码:', allContainers.map(c => c.system_code));
      
      // 在所有容器中查找精确匹配
      const exactMatch = allContainers.find((container: any) => 
        normalizeScScanValue(container.system_code) === normalizedContainerCode
      );
      
      console.log('精确匹配检查:');
      console.log('- 查找的编码:', normalizedContainerCode);
      console.log('- 精确匹配结果:', exactMatch);
      
      if (exactMatch) {
        const container = exactMatch;
        console.log('找到精确匹配的容器:');
        console.log('- 容器对象:', container);
        console.log('- 容器键:', Object.keys(container || {}));
        console.log('- 容器编码:', container.system_code);
        console.log('- 当前仓位:', container.current_location);
        console.log('- 当前仓位类型:', typeof container.current_location);
        
        // 检查当前仓位状态
        let currentLocationCode: string | null = null;
        let hasLocation = false;
        
        if (container.current_location) {
          if (typeof container.current_location === 'number') {
            // current_location是数字ID，表示容器已有仓位
            hasLocation = true;
            currentLocationCode = `ID:${container.current_location}`;
            console.log('- 容器已有仓位ID:', container.current_location);
          } else if (typeof container.current_location === 'object' && container.current_location.code) {
            // current_location是对象，包含code属性
            hasLocation = true;
            currentLocationCode = container.current_location.code;
            console.log('- 容器已有仓位编码:', currentLocationCode);
          } else {
            // 其他情况，视为无仓位
            hasLocation = false;
            console.log('- 容器仓位状态异常:', container.current_location);
          }
        } else {
          hasLocation = false;
          console.log('- 容器无仓位');
        }
        
        console.log('- 最终仓位状态:', hasLocation ? '已有仓位' : '无仓位');
        console.log('- 提取的仓位编码:', currentLocationCode);
        
        const result = {
          exists: true,
          current_location: hasLocation ? currentLocationCode : null
        };
        
        console.log('验证结果:', result);
        console.log('=== 容器验证完成 ===');
        return result;
      } else {
        console.log('未找到精确匹配的容器:');
        console.log('- 请求的编码:', normalizedContainerCode);
        console.log('- 验证的总容器数量:', allContainers.length);
        console.log('- 验证的页数:', pageCount);
        
        const result = {
          exists: false,
          current_location: null
        };
        
        console.log('验证结果:', result);
        console.log('=== 容器验证完成 ===');
        return result;
      }
    } catch (error: any) {
      console.error('容器验证失败:');
      console.error('- 错误对象:', error);
      console.error('- 错误类型:', typeof error);
      console.error('- 错误消息:', error.message);
      
      if (error.response) {
        console.error('- 响应状态码:', error.response.status);
        console.error('- 响应数据:', error.response.data);
        console.error('- 响应头:', error.response.headers);
      } else if (error.request) {
        console.error('- 请求已发送但无响应:', error.request);
      } else {
        console.error('- 其他错误:', error);
      }
      
      console.log('=== 容器验证失败 ===');
      throw error;
    }
  };

  // 创建上架记录
  const createPutAwayRecords = async (locationCode: string) => {
    const { containers } = currentBatch;
    
    if (containers.length === 0) {
      console.log('没有容器需要上架，清空扫描框并聚焦');
      // 清空扫描框并聚焦，不显示提示信息
      setScanValue('');
      setTimeout(() => {
        scanInputRef.current?.focus();
      }, 0);
      return;
    }

    // 添加调试日志
    console.log('=== 创建上架记录调试信息 ===');
    console.log('容器编码列表:', containers);
    console.log('仓位编码:', locationCode);
    console.log('当前批次状态:', currentBatch);

    try {
      const response = await request.post('/api/v1/storage/put-away/create/', {
        container_codes: containers,
        location_code: locationCode,
        remarks: ''
      });

      const result = response.data;
      console.log('API响应成功:', result);

      if (result.success) {
        message.success(`成功创建 ${containers.length} 条上架记录`);
        
        // 刷新记录列表
        fetchPutAwayRecords();
        
        // 清空当前批次
        setCurrentBatch({ containers: [], location: null });
        
        // 清空扫描历史（只保留未上架的容器）
        clearScanHistoryAfterPutAway(containers);
        
        // 自动清空扫描框并聚焦，准备下次扫描
        setScanValue('');
        setTimeout(() => {
          scanInputRef.current?.focus();
        }, 0);
      } else {
        message.error(result.message || '创建上架记录失败');
      }
    } catch (error: any) {
      console.error('创建上架记录失败:', error);
      
      // 处理不同类型的错误
      if (error.response) {
        // 服务器返回了错误响应
        const { status, data } = error.response;
        
        console.log('=== 错误响应详情 ===');
        console.log('状态码:', status);
        console.log('错误数据:', data);
        console.log('错误类型:', typeof data);
        console.log('错误字段:', Object.keys(data || {}));
        
        if (status === 400) {
          // 验证错误
          if (data.errors) {
            // 检查是否是仓位不存在的错误
            const hasLocationError = data.errors.location_code && 
              data.errors.location_code.some((msg: string) => msg.includes('不存在'));
            
            if (hasLocationError) {
              console.log('仓位不存在，清空扫描框并聚焦，保留容器记录');
              message.error('仓位不存在，请检查编码');
              // 清空扫描框并聚焦，不显示错误提示，保留容器记录
              setScanValue('');
              setTimeout(() => {
                scanInputRef.current?.focus();
              }, 0);
              return;
            }
            
            // 显示具体的验证错误
            const errorMessages = Object.entries(data.errors).map(([field, messages]: [string, any]) => {
              if (Array.isArray(messages)) {
                return `${field}: ${messages.join(', ')}`;
              }
              return `${field}: ${messages}`;
            }).join('\n');
            
            message.error(`验证失败:\n${errorMessages}`);
          } else if (data.message) {
            // 显示错误消息
            message.error(data.message);
          } else {
            message.error('请求数据验证失败，请检查输入');
          }
        } else if (status === 401) {
          message.error('认证失败，请重新登录');
        } else if (status === 403) {
          message.error('权限不足');
        } else if (status === 500) {
          message.error('服务器内部错误');
        } else {
          message.error(`请求失败 (${status}): ${data.message || '未知错误'}`);
        }
      } else if (error.request) {
        // 请求已发送但没有收到响应
        message.error('网络连接失败，请检查网络');
      } else {
        // 其他错误
        message.error('网络错误，请重试');
      }
    }
  };

  // 清空扫描历史（只保留未上架的容器）
  const clearScanHistoryAfterPutAway = (putAwayContainers: string[]) => {
    setScanHistory(prev => {
      // 过滤掉已上架的容器，只保留未上架的容器和仓位
      return prev.filter(item => {
        // 如果是容器且已上架，则过滤掉
        if (item.type === 'container' && putAwayContainers.includes(item.value)) {
          return false;
        }
        // 如果是仓位，也过滤掉（因为已经完成上架）
        if (item.type === 'location') {
          return false;
        }
        // 保留未上架的容器
        return true;
      });
    });
  };

  // 清空当前批次
  const clearCurrentBatch = () => {
    setCurrentBatch({ containers: [], location: null });
    message.info('已清空当前批次');
  };

  // 清空扫描历史
  const clearScanHistory = () => {
    setScanHistory([]);
    setCurrentBatch({ containers: [], location: null });
    message.info('已清空扫描历史');
  };

  const clearIocCurrentBatch = () => {
    setIocCurrentBatch({ containers: [], location: null, instructionId: null, instructionCode: null });
    message.info('已清空 IOC 当前批次');
  };

  const clearIocScanHistory = () => {
    setIocScanHistory([]);
    setIocCurrentBatch({ containers: [], location: null, instructionId: null, instructionCode: null });
    message.info('已清空 IOC 扫描历史');
  };

  // 获取上架记录
  const fetchPutAwayRecords = async (page = tablePagination.current, pageSize = tablePagination.pageSize) => {
    try {
      const params: any = { page, page_size: pageSize };
      // 保障：仓库员工/主管在未显式选择时，默认按分配仓库过滤
      const effectiveWarehouseCode = (restrictedByWarehouseRole && !tableFilters.warehouseCode && warehousesForFilter.length > 0)
        ? warehousesForFilter[0].code
        : tableFilters.warehouseCode;
      // 预留筛选参数（后端如未实现会忽略）
      if (tableFilters.customerId) params.customer_id = tableFilters.customerId;
      if (tableFilters.projectCode) params.project_code = tableFilters.projectCode;
      if (tableFilters.containerCode) params.container_code = tableFilters.containerCode;
      if (tableFilters.locationCode) params.location_code = tableFilters.locationCode;
      if (effectiveWarehouseCode) params.warehouse_code = effectiveWarehouseCode;
      if (tableDateRange && tableDateRange[0] && tableDateRange[1]) {
        try {
          params.created_at_after = tableDateRange[0].format ? tableDateRange[0].format('YYYY-MM-DD') : tableDateRange[0];
          params.created_at_before = tableDateRange[1].format ? tableDateRange[1].format('YYYY-MM-DD') : tableDateRange[1];
        } catch {}
      }
      const response = await request.get('/api/v1/storage/put-away/records/', { params });

      const result = response.data;

      if (result.success) {
        // 转换数据格式以匹配前端数据结构
        const formattedRecords: PutAwayRecord[] = result.data.records.map((record: any) => ({
          id: record.id.toString(),
          containerCode: record.container_code,
          locationCode: record.location_code,
          projectCode: record.project_code,
          projectName: record.project_name,
          customerId: record.customer_id,
          customerUsername: record.customer_username,
          warehouseName: record.warehouse_name,
          putAwayTime: record.put_away_time,
          putAwayTimeRaw: record.put_away_time,
          operator: record.operator_name,
          status: 'completed',
          remarks: record.remarks || ''
        }));
        
        setRecords(formattedRecords);
        setTablePagination(prev => ({ ...prev, current: result.data.page, pageSize: result.data.page_size, total: result.data.total }));
      } else {
        message.error(result.message || '获取上架记录失败');
      }
    } catch (error: any) {
      console.error('获取上架记录失败:', error);
      
      if (error.response) {
        const { status, data } = error.response;
        if (data.message) {
          message.error(data.message);
        } else {
          message.error(`获取记录失败 (${status})`);
        }
      } else {
        message.error('网络错误，请重试');
      }
    }
  };

  // 处理编辑
  const handleEdit = (record: PutAwayRecord) => {
    setEditingRecord(record);
    editForm.setFieldsValue({
      containerCode: record.containerCode,
      locationCode: record.locationCode,
      remarks: record.remarks || ''
    });
    setIsEditModalVisible(true);
  };

  // 删除上架记录
  const handleDelete = async (record: PutAwayRecord) => {
    Modal.confirm({
      title: t('putAway.deleteConfirm.title'),
      content: t('putAway.deleteConfirm.content', { containerCode: record.containerCode }),
      okText: t('putAway.deleteConfirm.ok'),
      cancelText: t('putAway.deleteConfirm.cancel'),
      onOk: async () => {
        try {
          const response = await request.delete(`/api/v1/storage/put-away/records/${record.id}/delete/`);

          const result = response.data;

          if (result.success) {
            message.success(t('putAway.messages.deleteSuccess'));
            // 刷新记录列表
            fetchPutAwayRecords();
          } else {
            message.error(result.message || '删除失败');
          }
        } catch (error: any) {
          console.error('删除上架记录失败:', error);
          
          if (error.response) {
            const { status, data } = error.response;
            if (data.message) {
              message.error(data.message);
            } else {
              message.error(`删除失败 (${status})`);
            }
          } else {
            message.error('网络错误，请重试');
          }
        }
      }
    });
  };

  // 保存编辑
  const handleSaveEdit = async () => {
    try {
      const values = await editForm.validateFields();
      if (editingRecord) {
        // 调用后端API更新上架记录
        const response = await request.put(`/api/v1/storage/put-away/records/${editingRecord.id}/update/`, {
          container_code: values.containerCode,
          location_code: values.locationCode,
          remarks: values.remarks || ''
        });

        if (response.data.success) {
          message.success('上架记录更新成功');
          
          // 刷新记录列表，获取最新数据
          fetchPutAwayRecords();
          
          // 关闭编辑模态框
          setIsEditModalVisible(false);
          setEditingRecord(null);
          editForm.resetFields();
        } else {
          message.error(response.data.message || '更新失败');
        }
      }
    } catch (error: any) {
      console.error('更新上架记录失败:', error);
      
      if (error.response) {
        const { status, data } = error.response;
        if (data.errors) {
          // 显示具体的验证错误
          const errorMessages = Object.entries(data.errors).map(([field, messages]: [string, any]) => {
            if (Array.isArray(messages)) {
              return `${field}: ${messages.join(', ')}`;
            }
            return `${field}: ${messages}`;
          }).join('\n');
          message.error(`验证失败:\n${errorMessages}`);
        } else if (data.message) {
          message.error(data.message);
        } else {
          message.error('更新失败，请检查输入');
        }
      } else {
        message.error('网络错误，请重试');
      }
    }
  };

  // 新增：创建新仓位
  const handleCreateLocation = () => {
    console.log('打开创建仓位模态框');
    console.log('当前仓库列表:', warehouses);
    setIsCreateLocationModalVisible(true);
    createLocationForm.resetFields();
    
    // 按角色和分配仓库设置默认值
    const assignedIds = (permissions?.assigned_warehouse_ids || []) as number[];
    const restricted = isWarehouseStaff() || isWarehouseSupervisor();
    const available = restricted
      ? warehouses.filter(w => assignedIds.includes(w.id))
      : warehouses;
    if (available.length > 0) {
      const def = available[0];
      console.log('设置默认仓库(按权限):', def?.code, def?.name);
      setTimeout(() => {
        createLocationForm.setFieldsValue({ warehouse: def.code });
      }, 0);
    }
  };

  // 新增：保存新仓位
  const handleSaveLocation = async () => {
    try {
      const values = await createLocationForm.validateFields();
      console.log('创建新仓位:', values);
      
      // 调用后端API创建新仓位
      const response = await request.post('/api/v1/storage/locations/create/', {
        warehouse: values.warehouse,
        location_code: values.locationCode,
        location_type: values.locationType,
        location_attribute: values.locationAttribute,
        remarks: values.remarks || ''
      });
      
      if (response.data.success) {
        message.success('新仓位创建成功');
        console.log('仓位创建成功:', response.data.data);
        
        // 关闭模态框并重置表单
        setIsCreateLocationModalVisible(false);
        createLocationForm.resetFields();
        
        // 刷新仓位列表，让新创建的仓位立即显示
        fetchLocations();
      } else {
        message.error(response.data.message || '创建仓位失败');
      }
    } catch (error: any) {
      console.error('创建仓位失败:', error);
      
      if (error.response) {
        const { status, data } = error.response;
        if (data.errors) {
          // 显示具体的验证错误
          const errorMessages = Object.entries(data.errors).map(([field, messages]: [string, any]) => {
            if (Array.isArray(messages)) {
              return `${field}: ${messages.join(', ')}`;
            }
            return `${field}: ${messages}`;
          }).join('\n');
          message.error(`验证失败:\n${errorMessages}`);
        } else if (data.message) {
          message.error(data.message);
        } else {
          message.error('创建仓位失败，请检查输入');
        }
      } else {
        message.error('网络错误，请重试');
      }
    }
  };

  // 获取仓库列表
  const fetchWarehouses = async () => {
    setIsLoadingWarehouses(true);
    try {
      const response = await request.get('/api/v1/storage/warehouses/');
      if (response.data.success) {
        console.log('获取到的仓库列表:', response.data.data);
        setWarehouses(response.data.data);
      } else {
        message.error('获取仓库列表失败');
      }
    } catch (error: any) {
      console.error('获取仓库列表失败:', error);
      message.error('网络错误，请重试');
    } finally {
      setIsLoadingWarehouses(false);
    }
  };

  // 获取仓位列表
  const fetchLocations = async () => {
    setIsLoadingLocations(true);
    try {
      const response = await request.get('/api/v1/storage/locations/');
      if (response.data.success) {
        console.log('获取到的仓位列表:', response.data.data);
        setLocations(response.data.data);
      } else {
        message.error('获取仓位列表失败');
      }
    } catch (error: any) {
      console.error('获取仓位列表失败:', error);
      message.error('网络错误，请重试');
    } finally {
      setIsLoadingLocations(false);
    }
  };

  // 获取所有容器
  const fetchAllContainers = async () => {
    setIsLoadingContainers(true);
    try {
      const response = await request.get('/api/v1/storage/containers/');
      // 兼容两种返回结构：{ success, data } 或 DRF 列表 { results, count }
      const dataList = Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data?.results)
          ? response.data.results
          : [];
      setAllContainers(dataList);
    } catch (error: any) {
      // 静默失败：仅记录日志，不再显示错误提示
      console.warn('获取所有容器失败(已静默):', error?.message || error);
    } finally {
      setIsLoadingContainers(false);
    }
  };

  // 组件挂载时获取上架记录
  useEffect(() => {
    fetchPutAwayRecords(1, tablePagination.pageSize);
    fetchIocPutAwayRecords(1, iocTablePagination.pageSize);
    fetchWarehouses(); // 组件挂载时获取仓库列表
    fetchLocations(); // 组件挂载时获取仓位列表
    fetchAllContainers(); // 组件挂载时获取所有容器
    // 预加载一页项目供下拉初始展示
    (async () => {
      try {
        setProjectLoading(true);
        const resp: any = await getProjectList({ page: 1, page_size: 20, ordering: 'project_code' });
        const results: any[] = resp?.data?.results || [];
        setProjectOptions(results.map(p => ({ value: p.project_code, label: `${p.project_code} - ${p.name}` })));
      } catch (e) {
        // 静默
      } finally {
        setProjectLoading(false);
      }
    })();
    // 预加载客户（仅主账号）
    (async () => {
      try {
        setCustomerLoading(true);
        const list: any[] = await getCustomerList({ onlyMain: true });
        setCustomerOptions((list || []).map(c => ({ value: String(c.id), label: c.username })));
      } catch (e) {
        // 静默
      } finally {
        setCustomerLoading(false);
      }
    })();
  }, []);

  // 模糊实时搜索（容器编码/仓位编码），忽略大小写由后端 icontains 处理
  useEffect(() => {
    if (fuzzySearchTimerRef.current) {
      clearTimeout(fuzzySearchTimerRef.current);
    }
    fuzzySearchTimerRef.current = setTimeout(() => {
      // 回到第一页并触发查询
      setTablePagination(prev => ({ ...prev, current: 1 }));
      fetchPutAwayRecords(1, tablePagination.pageSize);
    }, 400);
    return () => {
      if (fuzzySearchTimerRef.current) {
        clearTimeout(fuzzySearchTimerRef.current);
      }
    };
    // 仅监听容器编码与仓位编码输入的变化
  }, [tableFilters.containerCode, tableFilters.locationCode]);

  useEffect(() => {
    if (iocFuzzySearchTimerRef.current) {
      clearTimeout(iocFuzzySearchTimerRef.current);
    }
    iocFuzzySearchTimerRef.current = setTimeout(() => {
      setIocTablePagination((prev) => ({ ...prev, current: 1 }));
      fetchIocPutAwayRecords(1, iocTablePagination.pageSize);
    }, 400);
    return () => {
      if (iocFuzzySearchTimerRef.current) {
        clearTimeout(iocFuzzySearchTimerRef.current);
      }
    };
  }, [iocTableFilters.iocCode, iocTableFilters.locationCode, iocTableFilters.instructionCode]);

  // 自动搜索：所属项目/仓库 选择变化时自动触发查询（防抖）
  useEffect(() => {
    if (selectSearchTimerRef.current) {
      clearTimeout(selectSearchTimerRef.current);
    }
    selectSearchTimerRef.current = setTimeout(() => {
      setTablePagination(prev => ({ ...prev, current: 1 }));
      fetchPutAwayRecords(1, tablePagination.pageSize);
    }, 300);
    return () => {
      if (selectSearchTimerRef.current) {
        clearTimeout(selectSearchTimerRef.current);
      }
    };
  }, [tableFilters.customerId, tableFilters.projectCode, tableFilters.warehouseCode]);

  // 模糊实时搜索项目（远程）
  const handleSearchProjects = async (keyword: string) => {
    if (projectSearchTimerRef.current) {
      clearTimeout(projectSearchTimerRef.current);
    }
    projectSearchTimerRef.current = setTimeout(async () => {
      try {
        setProjectLoading(true);
        const resp: any = await getProjectList({ page: 1, page_size: 20, search: keyword || '', ordering: 'project_code' });
        const results: any[] = resp?.data?.results || [];
        setProjectOptions(results.map(p => ({ value: p.project_code, label: `${p.project_code} - ${p.name}` })));
      } catch (e) {
        // 静默
      } finally {
        setProjectLoading(false);
      }
    }, 300);
  };

  // 新增：当仓库列表加载完成后设置默认值
  useEffect(() => {
    console.log('仓库列表更新，当前仓库数量:', warehouses.length);
    if (warehouses.length > 0) {
      // 优先选择ID为1的仓库，其次选择US-1-NH代码的仓库，最后使用第一个仓库
      let defaultWarehouse = warehouses.find(w => w.id === 1);
      console.log('查找ID为1的仓库:', defaultWarehouse);
      
      if (!defaultWarehouse) {
        defaultWarehouse = warehouses.find(w => w.code === 'US-1-NH');
        console.log('查找US-1-NH代码的仓库:', defaultWarehouse);
      }
      if (!defaultWarehouse) {
        defaultWarehouse = warehouses[0];
        console.log('使用第一个仓库:', defaultWarehouse);
      }
      
      if (defaultWarehouse) {
        console.log('设置默认仓库:', defaultWarehouse.code);
        createLocationForm.setFieldsValue({
          warehouse: defaultWarehouse.code
        });
      }
    }
  }, [warehouses, createLocationForm]);

  // 表格列定义
  const columns = [
    {
      title: '仓储容器(SC)系统编码',
      dataIndex: 'containerCode',
      key: 'containerCode',
      width: 180,
      render: (value: string) => {
        const code = String(value || '').trim();
        if (!code) return '-';
        return (
          <Space size={6}>
            <span style={{ color: '#1677ff', fontWeight: 500 }}>{code}</span>
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => { void copyCode(code); }}
              style={{ padding: 0, width: 18, minWidth: 18, height: 18 }}
            />
          </Space>
        );
      },
    },
    {
      title: t('putAway.table.locationCode'),
      dataIndex: 'locationCode',
      key: 'locationCode',
      width: 150,
    },
    ...(!hideCustomer ? [{
      title: '所属客户',
      dataIndex: 'customer' as const,
      key: 'customer',
      width: 160,
      render: (_: any, record: PutAwayRecord) => (
        <span style={{ color: '#1890ff', fontWeight: 500 }}>
          {record.customerUsername || '-'}
        </span>
      )
    }] : []),
    {
      title: '所属项目',
      dataIndex: 'project',
      key: 'project',
      width: 180,
      render: (_: any, record: PutAwayRecord) => (
        <span>{record.projectCode ? `${record.projectCode}${record.projectName ? ` - ${record.projectName}` : ''}` : '-'}</span>
      )
    },
    {
      title: '仓库',
      dataIndex: 'warehouseName',
      key: 'warehouseName',
      width: 160,
      render: (text: string) => text || '-',
    },
    {
      title: t('putAway.table.putAwayTime'),
      dataIndex: 'putAwayTime',
      key: 'putAwayTime',
      width: 180,
      sorter: true,
      render: (_: any, record: PutAwayRecord) => (
        <TimezoneAwareTime
          time={record.putAwayTimeRaw || record.putAwayTime}
          format="YYYY-MM-DD HH:mm:ss"
          showTimezone={false}
        />
      )
    },
    {
      title: t('putAway.table.operator'),
      dataIndex: 'operator',
      key: 'operator',
      width: 100,
    },
    {
      title: '备注',
      dataIndex: 'remarks',
      key: 'remarks',
      width: 110,
      render: (remarks: string) => remarks ? (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {remarks.length > 20 ? `${remarks.substring(0, 20)}...` : remarks}
        </Text>
      ) : '-'
    },
    {
      title: t('putAway.table.actions'),
      key: 'actions',
      width: 120,
      render: (_: any, record: PutAwayRecord) => (
        <Button
          type="text"
          size="small"
          icon={<EditOutlined />}
          onClick={() => handleEdit(record)}
        >
          {t('putAway.table.edit')}
        </Button>
      )
    }
  ];

  const iocColumns = [
    {
      title: '指令产出容器(IOC)系统编码',
      dataIndex: 'iocCode',
      key: 'iocCode',
      width: 180,
      render: (value: string) => {
        const code = String(value || '').trim();
        if (!code) return '-';
        return (
          <Space size={6}>
            <span style={{ color: '#1677ff', fontWeight: 500 }}>{code}</span>
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => { void copyCode(code); }}
              style={{ padding: 0, width: 18, minWidth: 18, height: 18 }}
            />
          </Space>
        );
      },
    },
    {
      title: '来源指令',
      dataIndex: 'instructionCode',
      key: 'instructionCode',
      width: 160,
    },
    {
      title: '仓位编码',
      dataIndex: 'locationDisplay',
      key: 'locationDisplay',
      width: 200,
    },
    {
      title: '仓库',
      dataIndex: 'warehouseName',
      key: 'warehouseName',
      width: 160,
      render: (text: string) => text || '-',
    },
    {
      title: '上架时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      render: (_: any, record: IocPutAwayRecord) => (
        <TimezoneAwareTime
          time={record.updatedAtRaw || record.updatedAt}
          format="YYYY-MM-DD HH:mm:ss"
          showTimezone={false}
        />
      ),
    },
    {
      title: '操作员',
      dataIndex: 'operator',
      key: 'operator',
      width: 120,
      render: (text: string) => text || '-',
    },
    {
      title: '备注',
      dataIndex: 'remarks',
      key: 'remarks',
      width: 140,
      render: (remarks: string) => remarks ? (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {remarks.length > 20 ? `${remarks.substring(0, 20)}...` : remarks}
        </Text>
      ) : '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 110,
      render: (_: any, record: IocPutAwayRecord) => (
        <Button
          type="text"
          size="small"
          icon={<EditOutlined />}
          onClick={() => handleIocEdit(record)}
        >
          修改
        </Button>
      ),
    },
  ];

  return (
    <div className="p-6">
      <Title level={2}>{t('putAway.title')}</Title>
      <div className="mb-4" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Button
          type="text"
          onClick={() => setActiveTab('storage')}
          style={{
            height: 36,
            padding: '0 14px',
            borderRadius: 8,
            border: activeTab === 'storage' ? '1px solid #95de64' : '1px solid #d9d9d9',
            background: activeTab === 'storage' ? '#f6ffed' : '#ffffff',
            color: activeTab === 'storage' ? '#389e0d' : '#595959',
            fontWeight: activeTab === 'storage' ? 700 : 500,
          }}
        >
          仓储容器(SC)上架
        </Button>
        <Button
          type="text"
          onClick={() => setActiveTab('ioc')}
          style={{
            height: 36,
            padding: '0 14px',
            borderRadius: 8,
            border: activeTab === 'ioc' ? '1px solid #91d5ff' : '1px solid #d9d9d9',
            background: activeTab === 'ioc' ? '#e6f7ff' : '#ffffff',
            color: activeTab === 'ioc' ? '#096dd9' : '#595959',
            fontWeight: activeTab === 'ioc' ? 700 : 500,
          }}
        >
          指令产出容器(IOC)上架
        </Button>
        <div className="put-away-speech-mode" style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Tooltip title="中文 / English 只影响容器编码的播报；仓位编码固定用英文播报；关闭后都不播报">
            <span style={{ color: '#8c8c8c', fontSize: 12, cursor: 'help' }}>扫描播报</span>
          </Tooltip>
          <Select
            size="small"
            value={speechMode}
            style={{ width: 110 }}
            onChange={(v) => changeSpeechMode(v as PutAwaySpeechMode)}
            options={[
              { value: 'zh-CN', label: '中文' },
              { value: 'en-US', label: 'English' },
              { value: 'off', label: '关闭' },
            ]}
            disabled={!speechSupported}
          />
        </div>
      </div>
      
      {activeTab === 'storage' && (
      <>
      {/* 上部：扫描区域 */}
      <Card className="mb-6">
        <div className="flex items-center space-x-6 mb-4">
          <div className="flex-1" style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap', minWidth: 0 }}>
            <Search
              placeholder="请扫描容器编码或仓位编码"
              enterButton={<ScanOutlined />}
              size="large"
              value={scanValue}
              onChange={(e) => setScanValue(e.target.value)}
              onSearch={handleScan}
              style={{ maxWidth: 400 }}
              loading={isScanning}
              disabled={isScanning}
              ref={scanInputRef} // 添加ref
            />
            {/* 最近一次扫描成功的容器或仓位，大字显示，和播报一致 */}
            {renderLastScan(lastScScan, 'put-away-last-scan')}
          </div>
          
          <div>
            <Button 
              type="primary" 
              size="large"
              onClick={handleCreateLocation}
              icon={<PlusOutlined />}
              className="mr-3"
            >
              创建新仓位
            </Button>
            
            <Select
              key={locationSelectKey}
              placeholder="选择仓位"
              style={{ width: 200 }}
              size="large"
              loading={isLoadingLocations}
              showSearch
              allowClear
              onChange={(value) => {
                if (value) {
                  setScanValue(value);
                  
                  // 检查当前批次是否已有容器，如果有则自动执行上架
                  if (currentBatch.containers.length > 0) {
                    console.log('选择仓位后自动执行上架操作');
                    announceScan('storage', 'location', String(value));
                    // 延迟执行，确保扫描框值已更新
                    setTimeout(() => {
                      createPutAwayRecords(value);
                    }, 100);
                  } else {
                    // 如果没有容器，就像扫描仓位一样处理：清空扫描框并聚焦
                    console.log('批次无容器，选择仓位后清空扫描框并聚焦');
                    setScanValue('');
                    setTimeout(() => {
                      scanInputRef.current?.focus();
                    }, 0);
                  }
                  
                  // 每次选择后自动清空仓位列表（通过改变key强制重新渲染）
                  setLocationSelectKey(prev => prev + 1);
                }
              }}
              filterOption={(input, option) => {
                const label = option?.label;
                if (typeof label === 'string') {
                  return label.toLowerCase().includes(input.toLowerCase());
                }
                return false;
              }}
            >
              <Select.OptGroup label="临时仓位">
                {locations
                  .filter(location => location.location_attribute === 'temporary')
                  .map(location => (
                    <Select.Option 
                      key={location.id} 
                      value={location.code}
                      label={`${location.code} - ${location.warehouse_name}`}
                    >
                      {location.code} - {location.warehouse_name}
                    </Select.Option>
                  ))
                }
              </Select.OptGroup>
              <Select.OptGroup label="永久仓位">
                {locations
                  .filter(location => location.location_attribute === 'permanent')
                  .map(location => (
                    <Select.Option 
                      key={location.id} 
                      value={location.code}
                      label={`${location.code} - ${location.warehouse_name}`}
                    >
                      {location.code} - {location.warehouse_name}
                    </Select.Option>
                  ))
                }
              </Select.OptGroup>
            </Select>
          </div>
        </div>

        {/* 当前批次状态 */}
        {currentBatch.containers.length > 0 && (
          <Alert
            message={`当前批次：${currentBatch.containers.length} 个容器`}
            description={
              <div>
                <div className="mb-2">
                  <strong>已扫描容器：</strong>
                  {currentBatch.containers.map((code, index) => (
                    <Tag key={index} color="blue" className="mr-2">
                      {code}
                    </Tag>
                  ))}
                </div>
                <div>
                  <strong>等待扫描：</strong>
                  <Text type="warning">仓位编码</Text>
                </div>
              </div>
            }
            type="info"
            showIcon
            action={
              <Button size="small" onClick={clearCurrentBatch} icon={<ClearOutlined />}>
                清空批次
              </Button>
            }
            className="mb-4"
          />
        )}
      </Card>
      <DownloadFieldsModal
        visible={downloadVisible}
        loading={downloading}
        onCancel={() => setDownloadVisible(false)}
        hiddenKeys={Array.isArray(exportHidden) ? exportHidden : []}
        onConfirm={async (selectedFields) => {
          setDownloading(true);
          try {
            const params: any = { selected_fields: selectedFields.join(',') };
            if (tableFilters.customerId) params.customer_id = tableFilters.customerId;
            if (tableFilters.projectCode) params.project_code = tableFilters.projectCode;
            if (tableFilters.containerCode) params.container_code = tableFilters.containerCode;
            if (tableFilters.locationCode) params.location_code = tableFilters.locationCode;
            if (tableFilters.warehouseCode) params.warehouse_code = tableFilters.warehouseCode;
            if (tableDateRange && tableDateRange[0] && tableDateRange[1]) {
              try {
                params.created_at_after = tableDateRange[0].format ? tableDateRange[0].format('YYYY-MM-DD') : tableDateRange[0];
                params.created_at_before = tableDateRange[1].format ? tableDateRange[1].format('YYYY-MM-DD') : tableDateRange[1];
              } catch {}
            }
            // 通过带鉴权的请求获取二进制并触发下载
            const resp = await request.get('/api/v1/storage/put-away/records/download/', {
              params,
              responseType: 'blob'
            });
            const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `put_away_records_${new Date().toISOString().replace(/[-:.TZ]/g, '').slice(0,14)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            message.success('上架记录已开始下载');
          } catch (e) {
            message.error('下载失败，请稍后再试');
          } finally {
            setDownloading(false);
            setDownloadVisible(false);
          }
        }}
      />

      {/* 下部：上架记录表格 */}
      <Card>
        {/* 标题行 */}
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-3">
            <Title level={4} className="mb-0">上架记录(SC)</Title>
            <Text type="secondary">当前筛选：{tablePagination.total} 条</Text>
          </div>
          <div>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              disabled={tablePagination.total === 0}
              onClick={() => {
                if (tablePagination.total === 0) return;
                setDownloadVisible(true);
              }}
            >
              批量下载（{tablePagination.total} 条）
            </Button>
          </div>
        </div>
        {/* 筛选操作区：两行，第一行添加“所属客户”，并将“仓库”移到第二行首位 */}
        <div className="flex items-center gap-2 mb-2">
            {!hideCustomer && (
              <Select
              showSearch
              allowClear
              placeholder="所属客户（精确选择）"
              style={{ width: 240 }}
              value={tableFilters.customerId || undefined}
              onChange={(val) => setTableFilters(prev => ({ ...prev, customerId: val || '' }))}
              filterOption={(input, option) => {
                const label = option?.label as string | undefined;
                return label ? label.toLowerCase().includes(input.toLowerCase()) : false;
              }}
              options={customerOptions}
              loading={customerLoading}
              />
            )}
            <Select
              showSearch
              allowClear
              placeholder="所属项目（可输入模糊搜索）"
              style={{ width: 260 }}
              value={tableFilters.projectCode || undefined}
              onSearch={handleSearchProjects}
              onChange={(val) => setTableFilters(prev => ({ ...prev, projectCode: val || '' }))}
              filterOption={false}
              options={projectOptions}
              loading={projectLoading}
            />
            <Input
              placeholder="按容器编码筛选"
              allowClear
              style={{ width: 200 }}
              value={tableFilters.containerCode}
              onChange={(e) => setTableFilters(prev => ({ ...prev, containerCode: e.target.value }))}
            />
            <Input
              placeholder="按仓位编码筛选"
              allowClear
              style={{ width: 200 }}
              value={tableFilters.locationCode}
              onChange={(e) => setTableFilters(prev => ({ ...prev, locationCode: e.target.value }))}
            />
        </div>
        <div className="flex items-center gap-2 mb-4">
            <Select
              placeholder="选择仓库"
              allowClear
              style={{ width: 200 }}
              value={tableFilters.warehouseCode || undefined}
              onChange={(val) => setTableFilters(prev => ({ ...prev, warehouseCode: val || '' }))}
              options={warehousesForFilter.map(w => ({ value: w.code, label: `${w.name} (${w.code})` }))}
              disabled={restrictedByWarehouseRole}
            />
            <DatePicker.RangePicker
              allowClear
              format="YYYY-MM-DD"
              onChange={(vals) => setTableDateRange(vals as any)}
            />
            <Button type="primary" onClick={() => fetchPutAwayRecords(1, tablePagination.pageSize)}>
              搜索
            </Button>
            <Button
              onClick={() => {
                setTableFilters({ customerId: '', projectCode: '', containerCode: '', locationCode: '', warehouseCode: '' });
                setTableDateRange(null);
                setTablePagination(prev => ({ ...prev, current: 1 }));
                fetchPutAwayRecords(1, tablePagination.pageSize);
              }}
            >
              重置
            </Button>
        </div>
        
        <Table
          columns={columns}
          dataSource={records}
          rowKey="id"
          pagination={{
            current: tablePagination.current,
            pageSize: tablePagination.pageSize,
            total: tablePagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showQuickJumper: true,
            showTotal: (total, range) => `第${range[0]}-${range[1]}条,共${total}条记录`
          }}
          onChange={(pag, _filters, sorter) => {
            const nextPage = pag.current || 1;
            const nextSize = pag.pageSize || tablePagination.pageSize;
            // 解析排序
            let ordering: string | undefined;
            const sortObj = Array.isArray(sorter) ? sorter[0] : sorter;
            if (sortObj && sortObj.field === 'putAwayTime') {
              ordering = sortObj.order === 'ascend' ? 'put_away_time' : sortObj.order === 'descend' ? '-put_away_time' : undefined;
            }
            setTablePagination(prev => ({ ...prev, current: nextPage, pageSize: nextSize }));
            // 带上排序参数
            (async () => {
              try {
                const params: any = { page: nextPage, page_size: nextSize };
                if (ordering) params.ordering = ordering;
                if (tableFilters.customerId) params.customer_id = tableFilters.customerId;
                if (tableFilters.projectCode) params.project_code = tableFilters.projectCode;
                if (tableFilters.containerCode) params.container_code = tableFilters.containerCode;
                if (tableFilters.locationCode) params.location_code = tableFilters.locationCode;
                if (tableFilters.warehouseCode) params.warehouse_code = tableFilters.warehouseCode;
                if (tableDateRange && tableDateRange[0] && tableDateRange[1]) {
                  try {
                    params.created_at_after = tableDateRange[0].format ? tableDateRange[0].format('YYYY-MM-DD') : tableDateRange[0];
                    params.created_at_before = tableDateRange[1].format ? tableDateRange[1].format('YYYY-MM-DD') : tableDateRange[1];
                  } catch {}
                }
                const response = await request.get('/api/v1/storage/put-away/records/', { params });
                const result = response.data;
                if (result.success) {
                  const formatted: PutAwayRecord[] = result.data.records.map((record: any) => ({
                    id: record.id.toString(),
                    containerCode: record.container_code,
                    locationCode: record.location_code,
                    projectCode: record.project_code,
                    projectName: record.project_name,
                    warehouseName: record.warehouse_name,
                    putAwayTime: new Date(record.put_away_time).toLocaleString('zh-CN'),
                    operator: record.operator_name,
                    status: 'completed',
                    remarks: record.remarks || ''
                  }));
                  setRecords(formatted);
                  setTablePagination(prev => ({ ...prev, current: result.data.page, pageSize: result.data.page_size, total: result.data.total }));
                }
              } catch (e) {
                console.warn('排序加载失败', e);
              }
            })();
          }}
          scroll={{ x: 800 }}
        />
      </Card>
      </>
      )}

      {activeTab === 'ioc' && (
        <>
          <Card className="mb-6">
            <div className="flex items-center space-x-6 mb-4">
              <div className="flex-1" style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap', minWidth: 0 }}>
                <Search
                  placeholder="请扫描 IOC 编码或仓位编码"
                  enterButton={<ScanOutlined />}
                  size="large"
                  value={iocScanValue}
                  onChange={(e) => setIocScanValue(e.target.value)}
                  onSearch={handleIocScan}
                  style={{ maxWidth: 420 }}
                  loading={iocIsScanning}
                  disabled={iocIsScanning}
                  ref={iocScanInputRef}
                />
                {/* 最近一次扫描成功的 IOC 或仓位，大字显示，和播报一致 */}
                {renderLastScan(lastIocScan, 'put-away-last-scan-ioc')}
              </div>
              <div>
                <Button
                  type="primary"
                  size="large"
                  onClick={handleCreateLocation}
                  icon={<PlusOutlined />}
                  className="mr-3"
                >
                  创建新仓位
                </Button>
                <Select
                  placeholder="选择仓位（可选）"
                  style={{ width: 220 }}
                  size="large"
                  loading={isLoadingLocations}
                  showSearch
                  allowClear
                  onChange={(value) => {
                    if (!value) return;
                    setIocScanValue(value);
                    if (iocCurrentBatch.containers.length > 0) {
                      announceScan('ioc', 'location', String(value));
                      setTimeout(() => {
                        void submitIocPutAway(String(value));
                      }, 100);
                    } else {
                      setIocScanValue('');
                      setTimeout(() => iocScanInputRef.current?.focus(), 0);
                    }
                  }}
                  filterOption={(input, option) => {
                    const label = option?.label;
                    if (typeof label === 'string') return label.toLowerCase().includes(input.toLowerCase());
                    return false;
                  }}
                >
                  <Select.OptGroup label="临时仓位">
                    {locations
                      .filter(location => location.location_attribute === 'temporary')
                      .map(location => (
                        <Select.Option
                          key={`ioc-tmp-${location.id}`}
                          value={location.code}
                          label={`${location.code} - ${location.warehouse_name}`}
                        >
                          {location.code} - {location.warehouse_name}
                        </Select.Option>
                      ))
                    }
                  </Select.OptGroup>
                  <Select.OptGroup label="永久仓位">
                    {locations
                      .filter(location => location.location_attribute === 'permanent')
                      .map(location => (
                        <Select.Option
                          key={`ioc-perm-${location.id}`}
                          value={location.code}
                          label={`${location.code} - ${location.warehouse_name}`}
                        >
                          {location.code} - {location.warehouse_name}
                        </Select.Option>
                      ))
                    }
                  </Select.OptGroup>
                </Select>
              </div>
            </div>

            {iocCurrentBatch.containers.length > 0 && (
              <Alert
                message={`当前批次：${iocCurrentBatch.containers.length} 个 IOC`}
                description={(
                  <div>
                    <div className="mb-2">
                      <strong>来源指令：</strong>
                      <Tag color="purple">{iocCurrentBatch.instructionCode || '-'}</Tag>
                    </div>
                    <div className="mb-2">
                      <strong>已扫描 IOC：</strong>
                      {iocCurrentBatch.containers.map((code, index) => (
                        <Tag key={`ioc-batch-${index}`} color="blue" className="mr-2">
                          {code}
                        </Tag>
                      ))}
                    </div>
                    <div>
                      <strong>等待扫描：</strong>
                      <Text type="warning">仓位编码或临时仓位文本</Text>
                    </div>
                  </div>
                )}
                type="info"
                showIcon
                action={(
                  <Button size="small" onClick={clearIocCurrentBatch} icon={<ClearOutlined />}>
                    清空批次
                  </Button>
                )}
                className="mb-4"
              />
            )}
          </Card>

          <Card>
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-3">
                <Title level={4} className="mb-0">上架记录(IOC)</Title>
                <Text type="secondary">当前筛选：{iocTablePagination.total} 条</Text>
              </div>
            </div>
            <div className="flex items-center gap-2 mb-4">
              <Input
                placeholder="按IOC编码筛选"
                allowClear
                style={{ width: 220 }}
                value={iocTableFilters.iocCode}
                onChange={(e) => setIocTableFilters(prev => ({ ...prev, iocCode: e.target.value }))}
              />
              <Input
                placeholder="按来源指令编码筛选"
                allowClear
                style={{ width: 240 }}
                value={iocTableFilters.instructionCode}
                onChange={(e) => setIocTableFilters(prev => ({ ...prev, instructionCode: e.target.value }))}
              />
              <Input
                placeholder="按仓位筛选"
                allowClear
                style={{ width: 220 }}
                value={iocTableFilters.locationCode}
                onChange={(e) => setIocTableFilters(prev => ({ ...prev, locationCode: e.target.value }))}
              />
              <Button
                onClick={() => {
                  setIocTableFilters({ iocCode: '', locationCode: '', instructionCode: '' });
                  setIocTablePagination(prev => ({ ...prev, current: 1 }));
                  fetchIocPutAwayRecords(1, iocTablePagination.pageSize);
                }}
              >
                重置
              </Button>
            </div>
            <Table
              columns={iocColumns}
              dataSource={iocRecords}
              rowKey="id"
              pagination={{
                current: iocTablePagination.current,
                pageSize: iocTablePagination.pageSize,
                total: iocTablePagination.total,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showQuickJumper: true,
                showTotal: (total, range) => `第${range[0]}-${range[1]}条,共${total}条记录`,
              }}
              onChange={(pag) => {
                const nextPage = pag.current || 1;
                const nextSize = pag.pageSize || iocTablePagination.pageSize;
                setIocTablePagination(prev => ({ ...prev, current: nextPage, pageSize: nextSize }));
                fetchIocPutAwayRecords(nextPage, nextSize);
              }}
              scroll={{ x: 900 }}
            />
          </Card>
        </>
      )}

      {/* 编辑模态框 */}
      <Modal
        title="修改SC上架记录"
        open={isEditModalVisible}
        onOk={handleSaveEdit}
        onCancel={() => {
          setIsEditModalVisible(false);
          setEditingRecord(null);
          editForm.resetFields();
        }}
        okText={t('putAway.editModal.save')}
        cancelText={t('putAway.editModal.cancel')}
      >
        <Form
          form={editForm}
          layout="vertical"
        >
          <Form.Item
            label="SC系统编码"
            name="containerCode"
            rules={[{ required: true, message: t('putAway.editModal.containerCodePlaceholder') }]}
          >
            <Input disabled />
          </Form.Item>
          
          <Form.Item
            label={t('putAway.editModal.locationCode')}
            name="locationCode"
            rules={[{ required: true, message: t('putAway.editModal.locationCodePlaceholder') }]}
          >
            <Select
              placeholder={t('putAway.editModal.locationCodePlaceholder')}
              showSearch
              allowClear
              filterOption={(input, option) => {
                const label = option?.label;
                if (typeof label === 'string') {
                  return label.toLowerCase().includes(input.toLowerCase());
                }
                return false;
              }}
              onSearch={(value) => {
                // 支持扫描输入，自动设置值
                if (value && value.trim()) {
                  editForm.setFieldsValue({ locationCode: value.trim() });
                }
              }}
              options={locations.map(location => ({
                value: location.code,
                label: `${location.code} - ${location.warehouse_name} (${location.location_attribute === 'temporary' ? '临时' : '永久'})`
              }))}
            />
          </Form.Item>
          
          <Form.Item
            label="备注"
            name="remarks"
          >
            <Input.TextArea placeholder="请输入备注信息（可选）" rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="修改IOC上架记录"
        open={isIocEditModalVisible}
        onOk={handleSaveIocEdit}
        onCancel={() => {
          setIsIocEditModalVisible(false);
          setIocEditingRecord(null);
          iocEditForm.resetFields();
        }}
        okText={t('putAway.editModal.save')}
        cancelText={t('putAway.editModal.cancel')}
      >
        <Form form={iocEditForm} layout="vertical">
          <Form.Item
            name="iocCode"
            label="IOC系统编码"
          >
            <Input disabled />
          </Form.Item>
          <Form.Item
            name="locationCode"
            label="仓位编码"
            rules={[{ required: true, message: '请输入仓位编码' }]}
          >
            <Select
              placeholder="请选择或扫描仓位编码"
              showSearch
              allowClear
              optionFilterProp="label"
              filterOption={(input, option) => {
                const label = String(option?.label || '').toLowerCase();
                const value = String(option?.value || '').toLowerCase();
                const keyword = String(input || '').trim().toLowerCase();
                return !!keyword && (label.includes(keyword) || value.includes(keyword));
              }}
              onSearch={(value) => {
                const keyword = String(value || '').trim().toUpperCase();
                if (!keyword) return;
                const hit = (locations || []).find((loc) => String(loc?.code || '').trim().toUpperCase() === keyword);
                if (hit) {
                  iocEditForm.setFieldsValue({ locationCode: String(hit.code || '').trim() });
                }
              }}
              options={locations.map(location => ({
                value: location.code,
                label: `${location.code} - ${location.warehouse_name} (${location.location_attribute === 'temporary' ? '临时' : '永久'})`
              }))}
            />
          </Form.Item>
          <Form.Item
            name="remarks"
            label="备注"
          >
            <Input.TextArea placeholder="请输入备注信息（可选）" rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 新增：创建新仓位模态框 */}
      <Modal
        title="创建新仓位"
        open={isCreateLocationModalVisible}
        onOk={handleSaveLocation}
        onCancel={() => {
          setIsCreateLocationModalVisible(false);
          createLocationForm.resetFields();
        }}
        okText="创建"
        cancelText="取消"
      >
        <Form
          form={createLocationForm}
          layout="vertical"
          initialValues={{
            locationType: 'floor',
            locationAttribute: 'temporary',
            warehouse: (restrictedByWarehouseRole && warehousesForModal.length > 0)
              ? warehousesForModal[0].code
              : (warehouses.length > 0 ? warehouses[0]?.code : undefined)
          }}
        >
          <Form.Item
            label="仓库"
            name="warehouse"
            rules={[{ required: true, message: '请选择仓库' }]}
          >
            <Select 
              placeholder="请选择仓库" 
              loading={isLoadingWarehouses}
              options={(warehousesForModal.length > 0 ? warehousesForModal : warehouses).map(warehouse => ({
                value: warehouse.code,
                label: `${warehouse.name} (${warehouse.code})`
              }))}
              disabled={restrictedByWarehouseRole}
            />
          </Form.Item>
          
          <Form.Item
            label="仓位编码"
            name="locationCode"
            rules={[{ required: true, message: '请输入仓位编码' }]}
          >
            <Input placeholder="请输入仓位编码，如：A-01-01-01" />
          </Form.Item>
          
          <Form.Item
            label="仓位类型"
            name="locationType"
            rules={[{ required: true, message: '请选择仓位类型' }]}
          >
            <Select placeholder="请选择仓位类型">
              <Select.Option value="shelf">货架位</Select.Option>
              <Select.Option value="floor">地堆位</Select.Option>
              <Select.Option value="other">其他</Select.Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            label="属性"
            name="locationAttribute"
            rules={[{ required: true, message: '请选择仓位属性' }]}
          >
            <Select placeholder="请选择仓位属性">
              <Select.Option value="permanent">永久</Select.Option>
              <Select.Option value="temporary">临时</Select.Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            label="备注"
            name="remarks"
          >
            <Input.TextArea placeholder="请输入备注信息（可选）" rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PutAway;
